import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter, HostListener,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CcBaseComponent} from '../../../crystal-elements/cc-base.component';
import {FormFieldConfig} from '../../../model/api/form-field-config';
import {FormFieldGroupConfig} from '../../../model/api/form-field-group-config';
import {CommonFieldService} from '../../../services/common/common-field.service';
import {UserInfoStorageService} from '../../../services/auth/user-info-storage.service';
import {FieldRecord} from '../../../model/api/field-record';
import {User} from '../../../model/security/user';
import {FieldRequest} from '../../../model/api/field-request';
import {SecurityUserService} from '../../../services/security/security-user.service';
import {PasswordService} from '../../../services/security/password.service';
import {UserService} from '../../../services/security/user.service';
import {PasswordSecurity} from '../../../model/security/password-security';
import {CcFormDialogComponent} from "../../../crystal-elements/form-dialog/cc-form-dialog.component";
import TYPE = FormFieldConfig.TYPE;
import {Router} from "@angular/router";
import {CrystalClearFrontUrl} from "../../../shared/pages/crystal-clear-front-url";

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class UserFormComponent extends CcBaseComponent {

  // fields
  countries: FieldRecord<any>[];

  languages: FieldRecord<any>[];

  transPartKey: string = 'user';

  formLoading: boolean = true;

  formSubmitted: boolean = true;

  formConfig: FormFieldGroupConfig[] = [];

  dialogFormTitle: string = '';

  @Input()
  formRecord: User;

  loadingProgressValue: number = 100;

  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<any>();

  // tslint:disable-next-line:no-output-on-prefix
  @Output()
  onSave = new EventEmitter<any>();

  formErrorMessage: string;

  passwordSecurity: PasswordSecurity | undefined;

  @ViewChild('ccDialog')
  ccDialog: CcFormDialogComponent;

  constructor(cdref: ChangeDetectorRef,
              private userService: UserService,
              private passwordService: PasswordService,
              private securityUserService: SecurityUserService,
              private commonFieldService: CommonFieldService,
              private userInfoStorageService: UserInfoStorageService,
              private router: Router,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.commonFieldService.clearStorage();
  }

  protected initConfigs() {
    super.initConfigs();
    this.formConfig = [
      {
        group: '',
        fields: [
          {
            field: 'username',
            required: true,
            column: 3,
            type: TYPE.TEXT,
            disabled: value => true
          },
          {
            field: 'firstName',
            required: true,
            column: 3,
            type: TYPE.TEXT
          },
          {
            field: 'lastName',
            required: true,
            column: 3,
            type: TYPE.TEXT
          },
          {
            field: 'jobTitle',
            column: 3,
            type: TYPE.TEXT
          },
          {
            field: 'email',
            required: false,
            column: 4,
            type: TYPE.EMAIL,
            disabled: value => true
          },
          {
            field: 'country',
            filter: true,
            column: 3,
            type: TYPE.DROPDOWN,
            icon: record => record ? 'flag-icon flag-icon-' + record.toLowerCase() : 'fas fa-globe-europe',
            options: () => this.countries,
            changed: event => this.formRecord.country = event.value.toUpperCase()
          },
          {
            field: 'language',
            filter: true,
            column: 3,
            type: TYPE.DROPDOWN,
            icon: record => record ? 'flag-icon flag-icon-' + record.toLowerCase() : 'fas fa-globe-europe',
            options: () => this.languages,
            changed: event => this.formRecord.language = event.value.toUpperCase()
          }]
      },
      {
        group: 'password_user',
        fields:
          [{
            field: 'currentPassword',
            column: 4,
            type: TYPE.PASSWORD,
            feedback: false,
            validate: record => this.validateCurrentPassword()
          },
            {
              field: 'password',
              column: 4,
              type: TYPE.PASSWORD,
            },
            {
              field: 'confirmPassword',
              column: 4,
              type: TYPE.PASSWORD,
              validate: record => this.validatePassword()
            },
          ]
      },
    ];
  }

  protected initData() {
    super.initData();
    this.loadingProgressValue = 0;
    this.commonFieldService.getCountries(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.countries = this.getFields(fieldResult);
      setTimeout(() => this.ccDialog.reloadFieldValueMap());
      this.loadingProgressValue += 100 / 4;
    });
    this.commonFieldService.getLanguages(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.languages = this.getFields(fieldResult);
      setTimeout(() => this.ccDialog.reloadFieldValueMap());
      this.loadingProgressValue += 100 / 4;
    });
    this.userService.getPasswordSecurity().subscribe(passwordSecurity => {
      this.passwordSecurity = passwordSecurity;
      setTimeout(() => this.ccDialog.reloadFieldValueMap());
      this.loadingProgressValue += 100 / 4;
    });
  }

  onShow(): void {
    this.formErrorMessage = '';
    if (!this.formRecord) return;
    if (this.loadingProgressValue == 100)
      this.loadingProgressValue = -1;
    // this.initConfigs();
    this.formLoading = true;
    this.securityUserService.getUser(this.formRecord.username).subscribe(validation => {
      if (validation.success) {
        this.formRecord = {...validation.item};
        this.formRecord.originUsername = this.formRecord.username;
        this.formRecord.password = undefined;
        // setTimeout(() => {
        //   if (!this.formRecordCopy.jobTitle || this.formRecordCopy.jobTitle.length == 0)
        //     this.formRecordCopy.jobTitle = '';
        //   this.formRecordCopy.password = '';
        // }, 500);

        this.dialogFormTitle = this.getTranslation('page.' + this.transPartKey + '.dialog_title');
      } else {
        this.formErrorMessage = this.getTranslation(validation.message);
      }
      this.formLoading = false;
      if (this.loadingProgressValue >= 0)
        this.loadingProgressValue += 100 / 4;
      else
        this.loadingProgressValue = 100;
      setTimeout(() => this.ccDialog.reloadFieldValueMap());
    });
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  validateCurrentPassword() {
    if (this.formRecord)
      if (!this.formRecord.currentPassword && !this.formRecord.password && !this.formRecord.confirmPassword) {
        return '';
      } else if (this.formRecord.password && !this.formRecord.currentPassword) {
        return this.getTranslation('error.message.current_password_required');
      }
    return '';
  }

  validatePassword():string {
    if (this.formRecord && (this.formRecord.password || this.formRecord.confirmPassword)
      && !this.passwordService.checkPasswords(this.passwordSecurity, this.formRecord.password, this.formRecord.confirmPassword)) {
      const reason = this.passwordService.getFailureReason(this.passwordSecurity, this.formRecord.password, this.formRecord.confirmPassword);
      return reason ? this.translateService.instant(reason.key, reason.params) : '';
    }
    return '';
  }

  save() {
    if (this.formRecord.password && !this.formRecord.currentPassword) {
      this.formErrorMessage = this.getTranslation('error.message.current_password_required');
      return;
    }
    this.securityUserService.updateInfos(this.formRecord).subscribe(validation => {
      if (validation.success) {
        if (this.formRecord.password && this.formRecord.password.length > 0) {
          this.userInfoStorageService.logout();
          this.userInfoStorageService.login({
            login: this.formRecord.username,
            password: this.formRecord.password
          }).subscribe(() => {
            this.userInfoStorageService.refreshConnectedUserCacheInfo().subscribe(() => {
              this.router.navigateByUrl(this.router.url).then(() => {
                this.closeDialogForm();
              });
            });
          });
        } else {
          this.closeDialogForm();
        }
      } else {
        this.formErrorMessage = this.getTranslation(validation.message);
      }
    });
  }

}
